// API Response Types matching the backend models

export interface DashboardTask {
  task: string;
  dueDate: string;
  status: string;
}

// Enhanced task interface with full functionality (includes Deal Management System fields)
export interface EnhancedDashboardTask {
  // Core identifiers
  id: string;
  task: string;
  title?: string;
  description?: string;
  
  // Deal Management System fields
  dealType?: string;
  source?: string;
  planTitle?: string;
  planId?: string;
  
  // Mercury CRM opportunity data
  clientName?: string;
  loanAmount?: number;
  monthlyPayment?: number;
  loanType?: string;
  opportunityName?: string;
  
  // Assignment & AI automation
  isDealAssigned?: boolean;
  assignedTo: string;
  assignmentDetails?: string;
  assignmentCount?: number;
  allAssignees?: string[];
  canAIHandle?: boolean;
  aiReadiness?: string;
  automationPotential?: number;
  dealComplexity?: string;
  
  // Status & progress
  dueDate: string;
  dueDateFormatted?: string;
  status: string;
  percentComplete?: number;
  progressStatus?: string;
  isCompleted: boolean;
  completedDate?: string;
  
  // Financial context
  isFinancialDeal?: boolean;
  dealValue?: number;
  monthlyValue?: number;
  financialCategory?: string;
  
  // Scheduling & urgency
  isOverdue?: boolean;
  daysUntilDue?: number;
  daysOverdue?: number;
  timePeriod?: string;
  priority: string;
  urgencyLevel?: string;
  plannerPriority?: number;
  
  // Enhanced metadata
  tags?: string[];
  categories?: string[];
  importance?: string;
  
  // Timestamps
  createdDate: string;
  createdDateTime?: string;
  lastModified?: string;
  lastModifiedDateTime?: string;
}

// Request types for task operations
export interface CreateTaskRequest {
  title: string;
  description?: string;
  dueDate?: string;
  status?: string;
  priority?: string;
  assignedTo?: string;
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  dueDate?: string;
  status?: string;
  priority?: string;
  assignedTo?: string;
  isCompleted?: boolean;
}

export interface DashboardEmail {
  id: string; // REAL Microsoft Graph email ID
  subject: string;
  sender: string;
  senderEmail: string; // Added - full email address
  date: string;
  bodyPreview: string; // Added
  body?: string; // Full email body HTML
  bodyType?: string; // HTML or text
  isRead: boolean; // Added
  hasAttachments: boolean; // Added
  attachments: EmailAttachment[]; // Updated to proper type
  conversationId: string; // Added
  importance: string; // Added
  isFlagged?: boolean; // Added
  categories: string[]; // Added
}

// Enhanced email interface with full functionality
export interface EnhancedDashboardEmail {
  id: string;
  subject: string;
  sender: string;
  senderEmail: string;
  date: string;
  body: string;
  bodyType?: string; // HTML or text
  bodyPreview: string;
  isRead: boolean;
  hasAttachments: boolean;
  attachments?: EmailAttachment[];
  conversationId?: string;
  threadMessages?: EnhancedDashboardEmail[];
  importance?: string;
  isFlagged?: boolean;
  categories?: string[];
}

export interface EmailAttachment {
  id?: string;
  name: string;
  size: number;
  contentType?: string;
  isInline?: boolean;
  downloadUrl?: string;
}

export interface EmailReply {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  replyToMessageId: string;
}

export interface DashboardSummary {
  activeDeals: number;
  pendingTasks: number;
  unreadEmails: number;
  completedToday: number;
  lastUpdate: string;
}

export interface ChatRequest {
  message: string;
  userId: string;
  conversationId: string;
  userName?: string;
  userEmail?: string;
}

export interface ChatResponse {
  message: string;
  agentExecutions: AgentExecution[];
  suggestions: string[];
  requiresFollowUp: boolean;
}

export interface AgentExecution {
  agentName: string;
  purpose: string;
  success: boolean;
  executionTime: number;
}

// Planner task interface for Microsoft Planner integration
export interface PlannerTask {
  id: string;
  title: string;
  planId: string;
  planName: string;
  bucketId?: string;
  status: 'Not Started' | 'In Progress' | 'Completed';
  percentComplete: number;
  priority: number;
  dueDate?: string;
  createdDate?: string;
  assignedTo?: string;
}

// Dashboard client interface for Mercury CRM integration
export interface DashboardClient {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  status?: string;
  // Extended fields for people from client.md
  mercuryId?: string;
  title?: string;
  middleName?: string;
  preferredName?: string;
  dateOfBirth?: string;
  address?: string;
  clientFolder?: string;
  hasSyncedData?: boolean;
  lastSync?: string;
  deals?: DashboardClientDeal[];
  dealFolders?: string[];
  employment?: DashboardEmployment[];
  expenses?: DashboardExpense[];
  mercuryNotes?: string;
  source?: string;
}

// Deal summary in client.md
export interface DashboardClientDeal {
  name: string;
  folder: string;
  amount?: number;
  status?: string;
  transactionType?: string;
  transactionSubType?: string;
  mercuryId?: string;
  mercuryNotes?: string;
  applicants?: DealApplicant[];
  assets?: DealAsset[];
  liabilities?: DealLiability[];
}

// Employment info from client.md
export interface DashboardEmployment {
  employer?: string;
  position?: string;
  income?: number;
  type?: string;
}

// Expense info from client.md
export interface DashboardExpense {
  category?: string;
  amount?: number;
  frequency?: string;
}

// Deal applicant from deal.md
export interface DealApplicant {
  name: string;
  role?: string;
  partyType?: string;
  mercuryContactId?: string;
}

// Deal asset from Mercury
export interface DealAsset {
  name?: string;
  type?: string;
  value?: number;
}

// Deal liability from Mercury
export interface DealLiability {
  name?: string;
  type?: string;
  value?: number;
  details?: string;
}

// SharePoint file browser types
export interface SharePointBrowseResponse {
  success: boolean;
  currentPath: string;
  breadcrumbs: SharePointBreadcrumb[];
  items: SharePointItem[];
}

export interface SharePointBreadcrumb {
  name: string;
  path: string;
}

export interface SharePointItem {
  id: string;
  name: string;
  isFolder: boolean;
  size?: number;
  itemCount?: number;
  lastModified?: string;
  webUrl?: string;
  mimeType?: string;
  extension?: string | null;
}

export interface SharePointFilePreview {
  success: boolean;
  previewType: 'pdf' | 'image' | 'office' | 'text' | 'unknown';
  fileId?: string;
  fileName: string;
  content?: string;
  lastModified?: string;
}

// ==================== KANBAN DEAL TYPES ====================

export interface KanbanDealApplicant {
  personName: string;
  role?: string;
  mercuryContactId?: string;
  email?: string;
  phone?: string;
}

export interface KanbanDealDetails {
  loanAmount?: number;
  lender?: string;
  propertyAddress?: string;
  purchasePrice?: number;
  settlementDate?: string;
  loanType?: string;
  loanTermYears?: number;
}

export interface KanbanDealTask {
  description: string;
  isCompleted: boolean;
  assignedTo?: string;
}

export interface KanbanDealDocument {
  fileName: string;
  isReceived: boolean;
  personName?: string;
}

export interface KanbanDeal {
  id: string;
  clientFolder: string;
  dealFolder: string;
  displayName: string;
  mercuryId?: string;
  status: string;
  amount?: number;
  transactionType?: string;
  transactionSubType?: string;
  applicants: KanbanDealApplicant[];
  details: KanbanDealDetails;
  tasks: KanbanDealTask[];
  tasksCompleted: number;
  tasksTotal: number;
  documentChecklist: Record<string, KanbanDealDocument[]>;
  docsReceived: number;
  docsTotal: number;
  lastSync?: string;
  createdDate?: string;
  mercuryNotes?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
}

export interface KanbanStatusSummary {
  count: number;
  totalAmount: number;
}

export interface KanbanDealsResponse {
  deals: KanbanDeal[];
  statusCounts: Record<string, KanbanStatusSummary>;
  timestamp: string;
}

export const DEAL_STATUSES = [
  'Leads',
  'Information Gathering',
  'Broker Solution Pending',
  'File Preparation',
  'Broker Solution',
  'To be Lodged',
  'Lodged',
  'Conditional - Pre Approval',
  'Unconditional',
  'Pending Settlement',
  'Settled',
  'On Hold'
] as const;

export type DealStatus = typeof DEAL_STATUSES[number];
